import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import UserPersonalDetails from '../user/UserPersonalDetails';

import HomeAttentionStrip from './HomeAttentionStrip';
import HomeClock from './HomeClock';
import HomeEmptyState from './HomeEmptyState';
import { PANEL_ID } from './HomeSection';
import HomeSections from './HomeSections';
import { SECTION_TYPES } from './homeSectionSummary';
import HomeTabs, { VIEW_APPROVALS, VIEW_DETAILS, VIEW_HOME, VIEW_NOTIFICATIONS, VIEW_REQUESTS } from './HomeTabs';
import HomeTiles from './HomeTiles';
import MyApprovalsView from './myviews/MyApprovalsView';
import MyNotificationsView from './myviews/MyNotificationsView';
import MyRequestsView from './myviews/MyRequestsView';
import useNow from './useNow';
import YourAccessPanel from './YourAccessPanel';

import { getLocalHour } from 'Helpers/dateTimeService';
import { apiClient } from 'Helpers/network';

import Alert from 'CommonComponents/Alert';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import useQuery from 'Hooks/useQuery';

const MORNING_UNTIL_HOUR = 12;
const AFTERNOON_UNTIL_HOUR = 18;

const VIEWS = [VIEW_HOME, VIEW_REQUESTS, VIEW_APPROVALS, VIEW_NOTIFICATIONS, VIEW_DETAILS];

/**
 * The view named in `?view=`, or Home.
 *
 * Read once at module load. An unknown value falls back to Home rather than
 * leaving the strip pointing at a panel that does not exist — and a view the
 * user is not entitled to is dropped by HomeTabs, which hides its trigger AND
 * its panel from the same array.
 */
const requestedView = () => {
    const view = new URLSearchParams(window.location.search).get('view');

    return VIEWS.includes(view) ? view : VIEW_HOME;
};

/**
 * The greeting, keyed off the hour on the USER'S clock rather than the
 * browser's — a Sydney user working from London is greeted for their own day.
 */
const greeting = (name, now) => {
    const hour = getLocalHour(now);

    if (hour < MORNING_UNTIL_HOUR) {
        return $t('Good morning, {{name}}', { name });
    }

    if (hour < AFTERNOON_UNTIL_HOUR) {
        return $t('Good afternoon, {{name}}', { name });
    }

    return $t('Good evening, {{name}}', { name });
};

/**
 * Whether this user has any work at all, anywhere in the payload.
 *
 * A count that arrived at `0` counts as "no work" but not as "no capability" —
 * only the allocation check below decides whether the welcome state takes over.
 */
const hasAnyWork = (counts = {}) => Object.values(counts).some((count) => typeof count === 'number' && count > 0);

/**
 * Whether the user is allocated anything in this event.
 *
 * Read off the access block rather than off the counts: a user attached to a
 * department with an empty queue is a normal user having a quiet day, not a new
 * user with nothing assigned, and showing them the welcome state would be
 * wrong.
 */
const hasAnyAllocation = (access = {}) =>
    !!access.full_access ||
    (access.departments || []).length > 0 ||
    (access.venues || []).length > 0 ||
    (access.revenue_departments || []).length > 0 ||
    (access.general_revenue_departments || []).length > 0 ||
    (access.cost_centres || []).length > 0 ||
    (access.revenue_cost_centres || []).length > 0;

/**
 * Which list the single panel opens on when the page loads.
 *
 * PROCUREMENTS WHERE THE USER HOLDS IT, and otherwise the FIRST TILE THEY DO
 * hold, in tile order — not a hardcoded fallback to a second named type, and
 * never a type whose count key is absent. A user without the Procurement module
 * has no procurements tile to select, so defaulting to it would open a panel
 * over a list the section endpoint would refuse them.
 *
 * Undefined when the user holds no work type at all, which leaves the panel
 * unrendered rather than empty.
 */
const defaultOpenType = (counts = {}) => SECTION_TYPES.find((type) => typeof counts[type] === 'number');

export default function HomeIndex({
    entitlementsUrl,
    eventName,
    budgetVersion,
    userName,
    sectionUrlTemplate,
    sectionTypePlaceholder,
    detailUrlTemplate,
    detailStatusUrlTemplate,
    detailTypePlaceholder,
    detailIdPlaceholder,
    reportsUrl,
    approvalAgeThresholdDays,
    approvalsQueueUrl,
    requestsQueueUrl,
    notificationsUrl,
    notificationReadUrl,
    notificationsReadAllUrl,
    myViews = {},
}) {
    const now = useNow();

    const entitlements = useQuery({
        queryFn: () => apiClient.get(entitlementsUrl),
        displayErrorToast: false,
    });

    const payload = entitlements.data?.data;

    /* Bumped by either approval surface after an action; both watch it. */
    const [approvalsVersion, setApprovalsVersion] = useState(0);
    const access = payload?.access ?? {};
    const counts = payload?.counts ?? {};
    const deltas = payload?.deltas ?? {};
    const surfaces = payload?.surfaces ?? {};

    /* Which tab is open. `?view=` is kept in step with history.replaceState
       rather than pushState: switching tab is not a navigation the back button
       should have to walk, but the URL still names the tab so it can be shared
       and survives a refresh. */
    const [view, setView] = useState(requestedView);

    const openView = (next) => {
        // HomeTabs rebuilds its `tabs` array (and every tab object in it) on
        // each render, so the notify effect it wires through CommonComponents/
        // Tabs sees a new `activeTab` identity and calls this on every render,
        // not just an actual tab change. Bailing here — rather than only in
        // HomeTabs — keeps this the one place that decides what "unchanged"
        // means for the URL.
        if (next === view) {
            return;
        }

        setView(next);

        const url = new URL(window.location.href);

        if (next === VIEW_HOME) {
            url.searchParams.delete('view');
        } else {
            url.searchParams.set('view', next);
        }

        window.history.replaceState({}, '', url);
    };

    /* The tab actually shown, which is not always the one asked for.
       `?view=approvals` can be pasted by anyone, and the surfaces that decide
       which tabs exist arrive after the URL does. A view this user is not
       entitled to has no trigger AND no panel, so leaving it selected would put
       the strip over a blank page. Derived rather than corrected in an effect,
       so there is no frame in which the wrong tab is selected. */
    const entitledView = (candidate) =>
        ({
            [VIEW_HOME]: true,
            [VIEW_REQUESTS]: !!surfaces.requests_list,
            [VIEW_APPROVALS]: !!surfaces.approvals,
            [VIEW_NOTIFICATIONS]: !!surfaces.notifications,
            /* No surface gates this one: every user has a profile, and a flag
               that could withhold it would lock someone out of their own
               password form. */
            [VIEW_DETAILS]: true,
        })[candidate] ?? false;

    const activeView = entitledView(view) ? view : VIEW_HOME;

    /* WHICH LIST THE ONE PANEL HOLDS. Held here rather than inside the panel
       because three things choose it — a tile, an attention card, and the
       default below — and a panel that owned the choice could not be told. */
    const [openType, setOpenType] = useState(null);
    const [revealPanel, setRevealPanel] = useState(false);

    /* Chosen once the payload names the user's capabilities, and never
       overwritten afterwards: a refetch after an inline action must not throw
       away the list the user has since opened. */
    useEffect(() => {
        if (payload && openType === null) {
            setOpenType(defaultOpenType(counts) ?? null);
        }
    }, [payload]);

    /* Changing the panel from a tile or a card further up the page changes
       something the user may not be able to see, and for a keyboard or
       screen-reader user nothing has happened at all. Scrolling to the panel
       and moving focus into it is what makes the click perceivable. Runs as an
       effect so the panel is in the DOM by the time it is looked up. */
    const openPanel = (type) => {
        setOpenType(type);
        setRevealPanel(true);
    };

    useEffect(() => {
        if (!revealPanel) {
            return;
        }

        const panel = document.getElementById(PANEL_ID);
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        panel?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        panel?.focus({ preventScroll: true });

        setRevealPanel(false);
    }, [revealPanel]);

    const zeroAllocation = !!payload && !hasAnyAllocation(access) && !hasAnyWork(counts);

    /* Every list view re-reads the entitlement payload after an action, so the
       tile, the tab badge and the list itself cannot drift apart on screen. */
    const refreshEntitlements = () => entitlements.refetch?.();

    /* BOTH approval surfaces re-read, not just the one that acted. HomeTabs
       keeps a visited tab mounted, so the panel and the register are on screen
       together once the user has seen both; refreshing only the acting one
       leaves the other showing a row that has just been cleared, beside a tile
       count that has already moved. */
    const onApprovalActioned = () => {
        setApprovalsVersion((version) => version + 1);
        refreshEntitlements();
    };

    /* The Home content — now the first panel of the strip rather than the whole
       page. */
    const homePanel = () => (
        <div className="tw-flex tw-flex-col tw-gap-6">
            {/* Guarded on the payload as well as the allocation check: the strip
                also renders when the payload FAILED to load, and tiles, lists
                and an access panel built from empty defaults would tell the user
                they hold nothing rather than that nothing could be read. */}
            {!!payload && !zeroAllocation && (
                <>
                    <HomeAttentionStrip counts={counts} deltas={deltas} onOpen={openPanel} />
                    <HomeTiles counts={counts} deltas={deltas} openType={openType} onOpen={openPanel} />
                    <HomeSections
                        openType={openType}
                        counts={counts}
                        deltas={deltas}
                        sectionUrlTemplate={sectionUrlTemplate}
                        sectionTypePlaceholder={sectionTypePlaceholder}
                        detailUrlTemplate={detailUrlTemplate}
                        detailStatusUrlTemplate={detailStatusUrlTemplate}
                        detailTypePlaceholder={detailTypePlaceholder}
                        detailIdPlaceholder={detailIdPlaceholder}
                        approvalsQueueUrl={approvalsQueueUrl}
                        onViewAllApprovals={() => openView(VIEW_APPROVALS)}
                        onApprovalActioned={onApprovalActioned}
                        approvalsRefreshToken={approvalsVersion}
                        myViews={myViews}
                    />
                    <YourAccessPanel access={access} />
                </>
            )}

            {/* No "Your access" panel here: this user holds nothing on any axis
                it enumerates, so it could only render rows of "None". The empty
                state carries the summary of what they DO hold instead. */}
            {zeroAllocation && <HomeEmptyState eventName={eventName} access={access} reportsUrl={reportsUrl} />}
        </div>
    );

    const panels = {
        [VIEW_HOME]: homePanel,
        [VIEW_REQUESTS]: () => <MyRequestsView requestsQueueUrl={requestsQueueUrl} onQueueChange={refreshEntitlements} myViews={myViews} />,
        [VIEW_APPROVALS]: () => (
            <MyApprovalsView
                approvalsQueueUrl={approvalsQueueUrl}
                approvalAgeThresholdDays={approvalAgeThresholdDays}
                onQueueChange={onApprovalActioned}
                refreshToken={approvalsVersion}
                myViews={myViews}
            />
        ),
        [VIEW_NOTIFICATIONS]: () => (
            <MyNotificationsView
                notificationsUrl={notificationsUrl}
                markReadUrl={notificationReadUrl}
                markAllReadUrl={notificationsReadAllUrl}
                onQueueChange={refreshEntitlements}
                myViews={myViews}
            />
        ),
        [VIEW_DETAILS]: () => (
            <UserPersonalDetails
                fetchUrl={myViews.fetch_user_url}
                updateProfileUrl={myViews.update_profile_url}
                updateRegionalSettingsUrl={myViews.update_regional_settings_url}
                changePasswordUrl={myViews.change_password_url}
                userDetails={myViews.user_details ?? {}}
            />
        ),
    };

    const header = (
        <>
            <div className="tw-min-w-0">
                {/* The page defines no page-title, so inc/navbar.blade.php renders
                    no heading of its own and the greeting IS the document's
                    top-level heading, sitting in the heading bar. */}
                <h1 className="tw-truncate tw-font-bold tw-text-3xl tw-text-charcoal">{greeting(userName, now)}</h1>
                {/* The organisation and the budget version the rest of the
                    page is measured against, on ONE line. The version is
                    only named when the page was served with one selected. */}
                <p className="tw-mb-0 tw-truncate tw-text-0.5sm tw-text-charcoal-60">
                    {eventName}
                    {!!budgetVersion && (
                        <>
                            <span aria-hidden="true"> · </span>
                            <span className="tw-font-semibold tw-text-blue">{budgetVersion}</span>
                        </>
                    )}
                </p>
            </div>
            <div className="tw-shrink-0">
                <HomeClock now={now} />
            </div>
        </>
    );

    // Looked up once: the target is a static Blade-rendered element that
    // never unmounts while HomeIndex is mounted.
    const [headerTarget] = useState(() => document.getElementById('home-page-header'));

    return (
        <div className="tw-flex tw-flex-col tw-gap-6">
            {headerTarget ? (
                ReactDOM.createPortal(header, headerTarget)
            ) : (
                <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">{header}</div>
            )}

            {/* The FIRST load only. Every inline approval, cancel and mark-read
                re-reads this payload to keep the tiles and tab badges honest, and
                a full-width spinner above the strip on each of those would shift
                the whole page under the user's cursor mid-interaction. Once the
                payload exists, a refresh updates the numbers in place. */}
            {entitlements.loading && !payload && <LoadingSpinner size="lg" />}

            {!!entitlements.error && !entitlements.loading && (
                <Alert type="error">{$t('We could not load your home page. Please refresh to try again.')}</Alert>
            )}

            {/* The strip waits for the payload: it is the payload's `surfaces`
                that decide which tabs exist, and a strip rendered before it
                arrives would have to appear, then change shape.

                It renders on a FAILED payload too. My details is ungated on
                purpose — it is the user's own profile and password form — so
                withholding the whole strip when the entitlement call errors
                would take away the one tab no entitlement is allowed to gate.
                The work tabs hide themselves: `surfaces` defaults to empty. */}
            {(!!payload || (!!entitlements.error && !entitlements.loading)) && (
                <HomeTabs
                    surfaces={surfaces}
                    counts={counts}
                    deltas={deltas}
                    notifications={payload?.notifications}
                    activeView={activeView}
                    onViewChange={openView}
                    panels={panels}
                />
            )}
        </div>
    );
}

/**
 * Everything the reused row components and modals need, as ONE JSON attribute.
 *
 * A `data-*` attribute rather than forty more Blade `var` globals beside My
 * Dashboard's, where a single undefined one throws and kills every component on
 * the page. Parsed defensively: a malformed payload leaves the tabs working
 * with the URLs they do have rather than taking the whole page down.
 */
const parseMyViews = (raw) => {
    try {
        return JSON.parse(raw || '{}');
    } catch (error) {
        return {};
    }
};

if (document.getElementById('HomeIndex')) {
    var rootEl = document.getElementById('HomeIndex');
    ReactDOM.render(
        <App>
            <HomeIndex
                entitlementsUrl={rootEl.getAttribute('data-entitlements-url')}
                eventName={rootEl.getAttribute('data-event-name')}
                budgetVersion={rootEl.getAttribute('data-budget-version')}
                userName={rootEl.getAttribute('data-user-name')}
                sectionUrlTemplate={rootEl.getAttribute('data-section-url')}
                sectionTypePlaceholder={rootEl.getAttribute('data-section-type-placeholder')}
                detailUrlTemplate={rootEl.getAttribute('data-detail-url')}
                detailStatusUrlTemplate={rootEl.getAttribute('data-detail-status-url')}
                detailTypePlaceholder={rootEl.getAttribute('data-detail-type-placeholder')}
                detailIdPlaceholder={rootEl.getAttribute('data-detail-id-placeholder')}
                reportsUrl={rootEl.getAttribute('data-reports-url')}
                approvalAgeThresholdDays={Number(rootEl.getAttribute('data-approval-age-threshold-days'))}
                approvalsQueueUrl={rootEl.getAttribute('data-approvals-queue-url')}
                requestsQueueUrl={rootEl.getAttribute('data-requests-queue-url')}
                notificationsUrl={rootEl.getAttribute('data-notifications-url')}
                notificationReadUrl={rootEl.getAttribute('data-notification-read-url')}
                notificationsReadAllUrl={rootEl.getAttribute('data-notifications-read-all-url')}
                myViews={parseMyViews(rootEl.getAttribute('data-my-views'))}
            />
        </App>,
        rootEl,
    );
}
